import { defineStore } from "pinia";
import { AnalyticsV2ChartType, AnalyticsV2ElementType, ColumnSendTypeDescriptor, SelectionFilterGroupSendDescriptor, TagEntityType } from "@/models/etc/types";
import { CommonHttp } from "@/models/interfaces/http/common.http.request";
import { reactive, shallowReactive } from "vue";
import { ListSettings, ProjectionParentInChain, ProjectionSettings, SelectionSettings } from "@/models/interfaces/entity-settings";
import { ColumnDateRangeDto, ColumnSend, ColumnSendSelectable, ColumnSettingsSend } from "@/models/interfaces/entity-settings/columns/columns";
import { ProjectionRolesFilterGroupDto, SelectionFilterGroupSend } from "@/models/interfaces/entity-settings/filters/group-filters";
import { rootStore } from "./index";
import { findRecursive, foreachRecursive, mapRecursive } from "@/models/helpers/recursive";
import { generalStore } from "./general";
import { BaseListItem, ColumnGroupItem, ColumnListItem, getIdentityKind } from "@/components/common/base-list/interfaces/ListTypes";
import { getSettingsTypeName } from "@/models/helpers/type-names";
import { BaseItemTypes } from "@/components/dnd/ItemTypes";
import { graphSettingsStore } from "./graph";
import { auxStore } from "./auxillary";
import { createUnrefFn, useDebounceFn } from '@vueuse/core';
import { validateFilters, validateFormulas } from "./validation";
import { AnalyticsTableFormula } from "@/models/interfaces/entity-settings/parameters/parameters";
import _ from "lodash";
import { AnalyticsElementIdentity, AnalyticsElementKind } from "@/models/interfaces/entity-settings/charts/charts";
import { AnalyticsElementOptionsDto } from "../models/interfaces/entity-settings/filters/filters";

/**
 * selected item settings (bottom left panel) methods store
 */

const API_URL = process.env.VUE_APP_API_URL

export type SettingsState = {
    columns_max: number;
    group_max: number;
    loading: boolean;
    saving: boolean;
    // indication that not to update the model on @Watch
    mutating: boolean;
    // ignore the request result and don't mutate the model after the request is finished
    stop_request: boolean;
    // selection and list parameters
    selectionFilters: SelectionFilterGroupSend[];
    selectionColumns: ColumnSettingsSend;
    tableFormulas: AnalyticsTableFormula[];
    graphColumns: ColumnSettingsSend;
    // projection parameters
    projectionColumns: ColumnSettingsSend;
    projectionFilters: ProjectionRolesFilterGroupDto[]; // ProjectionFilterSend[];
    projectionParentFilters: SelectionFilterGroupSend[];
    projectionParentChain: ProjectionParentInChain[];
    projectionOptions: AnalyticsElementOptionsDto[];

    // raw data for dnd
    selectedColumnsList: ColumnGroupItem[];

    // model validation errors
    filterValidation: any[];
    formulaValidation: any[];

    // refresh functions
    // debounce in order to perform only last call if they are many
    debouncedColumnsRefresh: Function | undefined;
    debouncedFiltersRefresh: Function | undefined;

    reqQueue: Map<string, AbortController>;
};

export const settingsStore = defineStore({
  id: "settingsStore",
  state: () =>
    ({
        columns_max: 0,
        group_max: 0,
        loading: false,
        saving: false,
        // indication that not to update the model on @Watch
        mutating: false,
        // ignore the request result and don't mutate the model after the request is finished
        stop_request: false,
        // reactive filters change and update directly from interface
        selectionFilters: reactive([] as SelectionFilterGroupSend[]),
        projectionFilters: reactive([] as /*ProjectionFilterSend[]*/ProjectionRolesFilterGroupDto[]),
        tableFormulas: reactive([] as AnalyticsTableFormula[]),
        projectionParentFilters: reactive([] as SelectionFilterGroupSend[]),
        projectionParentChain: reactive([] as ProjectionParentInChain[]),
        projectionOptions: shallowReactive([] as AnalyticsElementOptionsDto[]),
        // shallow reactive columns update from interface via actions because there are too many 
        // Position property updates
        selectionColumns: shallowReactive({} as ColumnSettingsSend),
        projectionColumns: shallowReactive({} as ColumnSettingsSend),
        graphColumns: shallowReactive({} as ColumnSettingsSend),
        selectedColumnsList: reactive([] as ColumnGroupItem[]),

        filterValidation: reactive([]),
        formulaValidation: reactive([]),

        debouncedColumnsRefresh: createUnrefFn(function(th: any, sec: number = 1000) {
          const a = useDebounceFn((that: any) => {
            const genstore = generalStore()
            const graphstore = graphSettingsStore()

            if (genstore.selectedItem.elType === BaseItemTypes.SELECTION) {
              that.getSelectionSettings(genstore.selectedItem.Id, genstore.selectedItem.DestType, true)
            } else if (genstore.selectedItem.elType === BaseItemTypes.LIST) {
              that.getListSettings(genstore.selectedItem.Id, genstore.selectedItem.DestType, true)
            } else if (genstore.selectedItem.elType === BaseItemTypes.PROJECTION) {
              that.getProjectionSettings(genstore.selectedItem.Id, genstore.selectedItem.SrcType, genstore.selectedItem.DestType, true)
            } else if (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
              genstore.selectedItem.elType === BaseItemTypes.GRAPH) {
              graphstore.getGraphSettings({
                ChartId: genstore.selectedItem.Id,
                ChartType: genstore.selectedItem.elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : (
                  genstore.selectedItem.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : AnalyticsV2ChartType.Linear),
                Destination: genstore.selectedItem.DestType
              }, genstore.selectedItem.parent)
            }
          }, sec)

          a(th);
        }),

        debouncedFiltersRefresh: useDebounceFn((that: any) => {
            const genstore = generalStore()

            if (genstore.selectedItem.elType === BaseItemTypes.SELECTION) {
              that.getSelectionSettings(genstore.selectedItem.Id, genstore.selectedItem.DestType, true)
            } else if (genstore.selectedItem.elType === BaseItemTypes.LIST) {
              that.getListSettings(genstore.selectedItem.Id, genstore.selectedItem.DestType, true)
            } else if (genstore.selectedItem.elType === BaseItemTypes.PROJECTION) {
              that.getProjectionSettings(genstore.selectedItem.Id, genstore.selectedItem.SrcType, genstore.selectedItem.DestType, true)
            }
          }, 1000
        ),

        reqQueue: new Map(),
    } as SettingsState),

  actions: {
    setMaxColumnIndex(payload: number) {
      this.columns_max = payload
    },

    setMutating(payload: boolean) {
      this.mutating = payload
    },

    setMaxGroupIndex(payload: number) {
      this.group_max = payload
    },

    clearFiltersValidation() {
      this.filterValidation = []
    },

    clearFormulasValidation() {
      this.formulaValidation = []
    },

    validateFilters(arg: any) {
      this.filterValidation = reactive(validateFilters(arg))
      if (this.filterValidation.length) {
        console.log('filters not valid')
        this.stop_request = true
      }
    },

    validateFormulas(arg: any) {
      this.formulaValidation = reactive(validateFormulas(arg))
      if (this.formulaValidation.length) {
        this.stop_request = true
      }
    },

    // stopRequest() {
    //   this.stop_request = true
    //   console.log('clear at stop')
    //   this.clearSettings()
    // },

    // debounced refresh actions for using after update
    refreshColumnsAfter(sec: number = 1000) {
      this.debouncedColumnsRefresh && this.debouncedColumnsRefresh(this, sec);
    },

    refreshFiltersAfter() {
      this.debouncedFiltersRefresh && this.debouncedFiltersRefresh(this);
    },

    // get settings
    async getSelectionSettings(item_id: number, listType: AnalyticsV2ElementType, no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.filterValidation.length) {
        return
      }

      if (this.formulaValidation.length) {
        return
      }

      const rootstore = rootStore()
      this.stop_request = false
      this.saving = false

      const entkey = '0-'+listType+'-'+item_id;
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key != entkey) {
          // console.log('abort settings conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });
      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp()
      try {
        this.loading = !no_indication
        let response: any = undefined
        await http.get(`Analytics/Selections/GetSettings/${item_id}?selectionType=${listType}`, {
          signal: controller.signal,
          transformResponse: [function (data: any, headers: any) {
            
            return ''+data;
          }],
        }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log('error 24')
          this.reqQueue.delete(entkey);
        });

        if (response && (response.status === 200 || response.status === 201)) {
          const genstore = generalStore()
          auxStore().recordAnalyticsReadTableElement(genstore.selectedItem)
          // don't update data if genstore is loading again because this data is obsolette
          this.setMutating(true)
          // console.log('genstore.isLoading: '+genstore.isLoading+', setstore.stop_request:'+this.stop_request)
          if (!genstore.isLoading && !this.stop_request) {

            const a = function (str: string) {
              return str.replace(/[^\0-~]/g, function(ch) {
                  return "\\u" + ("0000" + ch.charCodeAt(0).toString(16)).slice(-4);
              });
            }

            const data: SelectionSettings = JSON.parse(a(response.data));
            
            // console.log(data)

            
            // reset old
            // reactive filters change and update directly from interface
            this.projectionFilters = reactive([] as /*ProjectionFilterSend[]*/ProjectionRolesFilterGroupDto[]);
            this.projectionParentFilters = reactive([] as SelectionFilterGroupSend[]);
            this.projectionParentChain = reactive([] as ProjectionParentInChain[]);
            // shallow reactive columns update from interface via actions because there are too many 
            // Position property updates
            this.projectionColumns = shallowReactive({} as ColumnSettingsSend);
            this.graphColumns = shallowReactive({} as ColumnSettingsSend);
            this.columns_max = 0;
            this.group_max = 0;

            this.selectionFilters = reactive(data.Filters);
            this.selectionColumns = shallowReactive(data.ColumnsSettings);

            this.projectionOptions = reactive(data.Options as AnalyticsElementOptionsDto[]);
            this.tableFormulas = reactive(data.TableFormulas);

            this.updateColumnsIndices()
            this.updateColumnSelection(undefined);
          }

          this.loading = false
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e));
        this.loading = false;
        this.reqQueue.delete(entkey);
      }

      return { response_ok: false }
    },

    // get settings
    async getListSettings(item_id: number, listType: AnalyticsV2ElementType, no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.filterValidation.length) {
        return
      }

      if (this.formulaValidation.length) {
        return
      }

      const rootstore = rootStore()
      this.stop_request = false
      this.saving = false

      const entkey = '1-'+listType+'-'+item_id;
      this.reqQueue.get(entkey)?.abort()
      this.reqQueue.delete(entkey)
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = !no_indication
        const response = await http.get(`Analytics/Lists/GetSettings/${item_id}?listType=${listType}`, {
          signal: controller.signal,
          transformResponse: [function (data: any, headers: any) {
            
            return ''+data;
          }],
        });
        this.reqQueue.delete(entkey)

        if (response.status === 200) {
          const genstore = generalStore()
          auxStore().recordAnalyticsReadTableElement(genstore.selectedItem)
          // don't update data if genstore is loading again because this data is obsolette
          this.setMutating(true)
          // console.log('genstore.isLoading: '+genstore.isLoading+', setstore.stop_request:'+this.stop_request)
          if (!genstore.isLoading && !this.stop_request) {

            const a = function (str: string) {
              return str.replace(/[^\0-~]/g, function(ch) {
                  return "\\u" + ("0000" + ch.charCodeAt(0).toString(16)).slice(-4);
              });
            }

            const data: ListSettings = JSON.parse(a(response.data));
            
            // console.log(data)

            
            // reset old
            // reactive filters change and update directly from interface
            this.projectionFilters = reactive([] as /*ProjectionFilterSend[]*/ProjectionRolesFilterGroupDto[]);
            this.projectionParentFilters = reactive([] as  SelectionFilterGroupSend[]);
            this.projectionParentChain = reactive([] as ProjectionParentInChain[]);
            // shallow reactive columns update from interface via actions because there are too many 
            // Position property updates
            this.projectionColumns = shallowReactive({} as ColumnSettingsSend);
            this.graphColumns = shallowReactive({} as ColumnSettingsSend);
            this.columns_max = 0;
            this.group_max = 0;

            this.selectionFilters = reactive(data.Filters);
            this.selectionColumns = shallowReactive(data.ColumnsSettings);
            this.tableFormulas = reactive(data.TableFormulas);
            this.projectionOptions = reactive(data.Options as AnalyticsElementOptionsDto[]);

            this.updateColumnsIndices()
            this.updateColumnSelection(undefined);
          }

          this.loading = false
          return { response_ok: true }
        }
      } catch (e) {
        this.reqQueue.delete(entkey)
        if ((e as any)?.name === 'CanceledError' || (e as any)?.code === 'ERR_CANCELED') return { response_ok: false }
        console.log(JSON.stringify(e))
        this.loading = false
      }

      return { response_ok: false }
    },

    async getProjectionSettings(item_id: number, s_type: AnalyticsV2ElementType, dest_type: AnalyticsV2ElementType, no_indication: boolean = false) {
      // don't refresh erroneous data
      if (this.filterValidation.length) {
        return
      }

      if (this.formulaValidation.length) {
        return
      }

      const rootstore = rootStore()
      this.stop_request = false
      this.saving = false

      const entkey = '2-'+s_type+'-'+dest_type+'-'+item_id;
      this.reqQueue.get(entkey)?.abort()
      this.reqQueue.delete(entkey)
      const controller = new AbortController()
      this.reqQueue.set(entkey, controller)

      console.log('get projection settings')
      const http = new CommonHttp()
      try {
        this.loading = !no_indication
        const response = await http.get(`Analytics/Projections/GetSettings/${item_id}?source=${s_type}&destination=${dest_type}`, { signal: controller.signal });
        this.reqQueue.delete(entkey)
        if (response.status === 200) {
          const genstore = generalStore()
          auxStore().recordAnalyticsReadTableElement(genstore.selectedItem)
          // don't update data if genstore is loading again because this data is obsolette
          this.setMutating(true)
          if (!genstore.isLoading && !this.stop_request) {
            const { data } = response;
            // reset old
            // reactive filters change and update directly from interface
            this.selectionFilters = reactive([] as SelectionFilterGroupSend[]);
            // shallow reactive columns update from interface via actions because there are too many 
            // Position property updates
            this.selectionColumns = shallowReactive({} as ColumnSettingsSend);
            this.graphColumns = shallowReactive({} as ColumnSettingsSend);
            this.columns_max = 0;
            this.group_max = 0;

            this.projectionFilters = reactive(data.Filters);
            this.tableFormulas = reactive(data.TableFormulas);
            this.projectionOptions = reactive(data.Options);
            this.projectionParentFilters = reactive(data.ParentFilters);
            this.projectionParentChain = reactive(data.ParentChain);
            this.projectionColumns = shallowReactive(data.ColumnsSettings);

            this.updateColumnsIndices()
            this.updateColumnSelection(undefined)
          }

          this.loading = false
          return { response_ok: true }
        }
      } catch (e) {
        this.reqQueue.delete(entkey)
        if ((e as any)?.name === 'CanceledError' || (e as any)?.code === 'ERR_CANCELED') return { response_ok: false }
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    setGraphColumns(arg: ColumnSettingsSend) {
      this.setMutating(true)
      // reset old
      // reactive filters change and update directly from interface
      this.selectionFilters = reactive([] as SelectionFilterGroupSend[]);
      this.tableFormulas = reactive([] as AnalyticsTableFormula[]);
      this.projectionOptions = reactive([] as AnalyticsElementOptionsDto[]);
      // shallow reactive columns update from interface via actions because there are too many 
      // Position property updates
      this.selectionColumns = shallowReactive({} as ColumnSettingsSend);
      this.projectionColumns = shallowReactive({} as ColumnSettingsSend);
      this.columns_max = 0;
      this.group_max = 0;

      this.projectionFilters = reactive([] as /*ProjectionFilterSend[]*/ProjectionRolesFilterGroupDto[]);
      this.projectionParentFilters = reactive([] as SelectionFilterGroupSend[]);
      this.projectionParentChain = reactive([] as ProjectionParentInChain[]);
      
      this.graphColumns = shallowReactive(arg);

      this.updateColumnsIndices()
      this.updateColumnSelection(undefined)
    },

    /**
     * updateColumnSettings method. Backend API method for updating columns of a chart, selection or a projection
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async updateColumnSettings(arg: BaseListItem, no_indication: boolean = false) {
      const rootstore = rootStore()
      // const graphstore = graphSettingsStore()
      this.saving = true

      const http = new CommonHttp()
      try {
        let http_url = "";
        let params = {}
        switch (arg.elType) {
          case BaseItemTypes.SELECTION:
            params = {
              Columns: this.selectionColumns.Columns as ColumnSend[]
            }
            http_url = `Analytics/Selections/EditColumnSettings/${arg.Id}?selectionType=${arg.DestType}`;
            break;
          case BaseItemTypes.LIST:
            params = {
              Columns: this.selectionColumns.Columns as ColumnSend[]
            }
            http_url = `Analytics/Lists/EditColumnSettings/${arg.Id}?listType=${arg.DestType}`;
            break;
          case BaseItemTypes.PROJECTION:
            params = {
              Columns: this.projectionColumns.Columns as ColumnSend[]
            }
            http_url = `Analytics/Projections/EditColumnSettings/${arg.Id}?source=${arg.SrcType}&destination=${arg.DestType}`;
            break;
          case BaseItemTypes.BAR_CHART:
          case BaseItemTypes.PIE_CHART:
          case BaseItemTypes.GRAPH:
            params = {
              ChartIdentity: {
                ChartId: arg.Id,
                ChartType: arg.elType === BaseItemTypes.PIE_CHART ? AnalyticsV2ChartType.Pie : 
                  (arg.elType === BaseItemTypes.BAR_CHART ? AnalyticsV2ChartType.Bar : AnalyticsV2ChartType.Linear),
                Destination: arg.DestType
              },
              ColumnsContainer: {
                Columns: this.graphColumns.Columns as ColumnSend[]
              }
            }
            http_url = `Analytics/Charts/EditColumnSettings/`;
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          this.saving = false
          throw new Error('Incorrect API URL');
        }

        // console.log(params)
        const response = await http.put(http_url, params);
        // console.log(response)
        this.saving = false
        if (response.status !== 200) {
          this.refreshColumnsAfter()
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.saving = false
        this.refreshColumnsAfter()
      }

      this.saving = false
      return { response_ok: false }
    },

    /**
     * updateColumnSettings method. Backend API method for updating columns of a chart, selection or a projection
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async updateTableFormulas(arg: any, no_indication: boolean = false) {
      if (this.formulaValidation.length) {
        console.log('table formulas are incorrect')
        console.log(this.formulaValidation)
        return
      }
      this.saving = true

      const rootstore = rootStore()
      // const graphstore = graphSettingsStore()

      const http = new CommonHttp()
      try {
        let http_url = `Analytics/General/EditTableFormulas`;
        let params = {}
        switch (arg.element.elType) {
          case BaseItemTypes.SELECTION:
            params = {
              Formulas: arg.Formulas,
              ElementIdentity: {
                ElementId: arg.element.Id,
                Destination: arg.element.DestType,
                Kind: AnalyticsElementKind.Selection
              } as AnalyticsElementIdentity
            }
            break;
          case BaseItemTypes.LIST:
            params = {
              Formulas: arg.Formulas,
              ElementIdentity: {
                ElementId: arg.element.Id,
                Destination: arg.element.DestType,
                Kind: AnalyticsElementKind.List
              } as AnalyticsElementIdentity
            }
            break;
          case BaseItemTypes.PROJECTION:
            params = {
              Formulas: arg.Formulas,
              ElementIdentity: {
                ElementId: arg.element.Id,
                Source: arg.element.SrcType,
                Destination: arg.element.DestType,
                Kind: AnalyticsElementKind.Projection
              } as AnalyticsElementIdentity
            }
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          this.saving = false
          throw new Error('Incorrect API URL');
        }

        // console.log(params)
        const response = await http.put(http_url, params);
        // console.log(response)
        this.saving = false
        if (response.status === 200) {
          // this.refreshColumnsAfter()
          return { response_ok: true }
        }
      } catch (e) {
        this.saving = false
        console.log(JSON.stringify(e))
      }

      this.saving = false
      return { response_ok: false }
    },

    /**
     * updateElementOptions method. Backend API method for updating AnalyticsElementOptionsDto
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async updateElementOptions(arg: any, no_indication: boolean = false) {
      this.saving = true

      const rootstore = rootStore()

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        let http_url = `Analytics/General/TableElementOptions`;
        let params = {}
        switch (arg.element.elType) {
           case BaseItemTypes.SELECTION:
            params = {
              Options: arg.Options,
              ElementIdentity: {
                ElementId: arg.element.Id,
                Destination: arg.element.DestType,
                Kind: AnalyticsElementKind.Selection
              } as AnalyticsElementIdentity
            }
            break;
          case BaseItemTypes.LIST:
            params = {
              Options: arg.Options,
              ElementIdentity: {
                ElementId: arg.element.Id,
                Destination: arg.element.DestType,
                Kind: AnalyticsElementKind.List
              } as AnalyticsElementIdentity
            }
            break;
          case BaseItemTypes.PROJECTION:
            params = {
              Options: arg.Options,
              ElementIdentity: {
                ElementId: arg.element.Id,
                Source: arg.element.SrcType,
                Destination: arg.element.DestType,
                Kind: AnalyticsElementKind.Projection
              } as AnalyticsElementIdentity
            }
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          this.saving = false
          throw new Error('Incorrect API URL');
        }

        // console.log(params)
        const response = await http.put(http_url, params);
        // console.log(response)
        this.saving = false
        if (response.status === 200) {
          // this.refreshColumnsAfter()
          return { response_ok: true }
        }
      } catch (e) {
        this.saving = false
        console.log(JSON.stringify(e))
      }

      this.saving = false
      return { response_ok: false }
    },

    async changeFormulaPosition(arg: any, f_id: number, oldpos: number, newpos: number) {
      if (this.formulaValidation.length) {
        console.log('table formulas are incorrect')
        console.log(this.formulaValidation)
        return
      }
      this.saving = true

      const rootstore = rootStore()
      // const graphstore = graphSettingsStore()

      const http = new CommonHttp()
      try {
        let http_url = `Analytics/General/ChangeFormulaPosition`;
        let params = {}
        switch (arg.elType) {
          case BaseItemTypes.SELECTION:
          case BaseItemTypes.LIST:
            params = {
              FormulaId: f_id,
              DesiredPosition: newpos,
              ElementIdentity: {
                ElementId: arg.Id,
                Destination: arg.DestType,
                Kind: getIdentityKind(arg)
              } as AnalyticsElementIdentity
            }
            break;
          case BaseItemTypes.PROJECTION:
            params = {
              FormulaId: f_id,
              DesiredPosition: newpos,
              ElementIdentity: {
                ElementId: arg.Id,
                Source: arg.SrcType,
                Destination: arg.DestType,
                Kind: getIdentityKind(arg)
              } as AnalyticsElementIdentity
            }
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          this.saving = false
          throw new Error('Incorrect API URL');
        }

        // console.log(params)
        const response = await http.put(http_url, params);
        // console.log(response)
        this.saving = false
        if (response.status === 200) {
          // change right in the interface
          // TODO
          // save
          this.refreshColumnsAfter(10)
          // this.refreshColumnsAfter()
          return { response_ok: true }
        }
      } catch (e) {
        this.saving = false
        console.log(JSON.stringify(e))
      }

      this.saving = false
      return { response_ok: false }
    },

    async setDefaultColumnSettings(arg: any) {
      this.saving = true

      const rootstore = rootStore()
      // const graphstore = graphSettingsStore()

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        let http_url = `Analytics/General/StoredSettings`;
        let params = {}
        switch (arg.elType) {
          case BaseItemTypes.SELECTION:
          case BaseItemTypes.LIST:
            params = {
              ElementId: arg.Id,
              Destination: arg.DestType,
              Kind: getIdentityKind(arg)
            } as AnalyticsElementIdentity
            break;
          case BaseItemTypes.PROJECTION:
            params = {
              ElementId: arg.Id,
              Source: arg.SrcType,
              Destination: arg.DestType,
              Kind: getIdentityKind(arg)
            } as AnalyticsElementIdentity
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          this.saving = false
          throw new Error('Incorrect API URL');
        }

        // console.log(params)
        const response = await http.put(http_url, params);
        // console.log(response)
        this.saving = false
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        this.saving = false
        console.log(JSON.stringify(e))
      }

      this.saving = false
      return { response_ok: false }
    },

    /**
     * updateFilterSettings method. Backend API method for updating filter settings of a selection or a projection
     * @param arg - new parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async updateFilterSettings(arg: BaseListItem, params: SelectionFilterGroupSend | /*ProjectionFilterSend*/ProjectionRolesFilterGroupDto, 
        no_indication: boolean = false) {
      // don't save erroneous data
      if (this.filterValidation.length) {
        return
      }
      this.saving = true
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        let http_url = "";
        switch (arg.elType) {
          case BaseItemTypes.SELECTION:
            http_url = `Analytics/Selections/EditFilters/${arg.Id}?selectionType=${arg.DestType}`;
            break;
          case BaseItemTypes.LIST:
            http_url = `Analytics/Lists/EditFilters/${arg.Id}?listType=${arg.DestType}`;
            break;
          case BaseItemTypes.PROJECTION:
            http_url = `Analytics/Projections/EditFilters/${arg.Id}?source=${arg.SrcType}&destination=${arg.DestType}`;
            break;
          default:
            http_url = ""
            break;
        }

        if (!http_url) {
          this.saving = false
          throw new Error('Incorrect API URL');
        }

        // console.log(params)
        const response = await http.put(http_url, params);
        // console.log(response)
        this.saving = false
        if (response.status === 200) {
          // this.refreshFiltersAfter()
          return { response_ok: true }
        } else {
          this.refreshFiltersAfter()
        }
      } catch (e) {
        this.saving = false
        console.log(JSON.stringify(e))
        this.refreshFiltersAfter()
      }

      this.saving = false
      return { response_ok: false }
    },

    // non-http functions

    reorderColumns(element: any) {
      console.log('reorder')
      const genstore = generalStore()

      // change Position field in original list
      const update_position = (arr: ColumnSendSelectable[], ef: ColumnListItem) => {
        foreachRecursive(function a(element: any, el_to_find: any): any | undefined {
          if (!element) return
          
          if (el_to_find && el_to_find.Descriptor !== 2 && el_to_find.compound_id === element.compound_id) {
            element.Position = el_to_find.Position
          }
          if (element.ChildColumns && element.ChildColumns.length) {
            foreachRecursive(a, el_to_find, element.ChildColumns)
          }
          return
        }, ef, arr, this.group_max)
      }

      const tmparr = this.selectedColumnsList

      // If group_id is not null then it means that these items should be inside this one item.
      // In this case recursively go through items and increment position
      
      // Refactor this code so that Position is incremented inside sub_items too.
      if (element && element.old_pos !== undefined && element.new_pos !== undefined) {
        const el1ind = tmparr.findIndex(el => {
          return (el.Descriptor !== ColumnSendTypeDescriptor.Composite) && 
          el.Selected && el.Position === element.old_pos
        })
        const el2ind = tmparr.findIndex(el => {
          return (el.Descriptor !== ColumnSendTypeDescriptor.Composite) && 
          el.Selected && el.Position === element.new_pos
        })

        // swap in reactive list
        const item = tmparr[el1ind]

        tmparr.splice(el1ind, 1)
        tmparr.splice(el2ind, 0, item)

        // Here positions in the list can interfece Position in sub_items. This is just to reorder global elements.
        // But after this.updateColumnSelection(undefined) Position should become global throughout sub_items too.
        // tmparr.forEach((el, i) => { el.Position = i })

        // use tmp_arr in order not to 
        let global_pos = 0;
        tmparr.forEach(element => {
          // index throughout child arrays
          // sort sub_items and then recalc global position
          if (element.sub_items) {
            element.Position = global_pos;

            element.sub_items = reactive(element.sub_items.sort((el1, el2) => {
              return (el1 as ColumnListItem).Position - (el2 as ColumnListItem).Position
            }))

            element.sub_items.forEach(sub => {
              sub.Position = global_pos;
              global_pos++

              // change Position field in original list
              if (genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) && 
                this.selectionColumns && this.selectionColumns.Columns) {
                update_position(this.selectionColumns.Columns, sub)
              } else if (genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION && 
                this.projectionColumns && this.projectionColumns.Columns) {
                update_position(this.projectionColumns.Columns, sub)
              } else if (genstore.selectedItem && 
                (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || 
                  genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
                  genstore.selectedItem.elType === BaseItemTypes.GRAPH) && 
                this.graphColumns && this.graphColumns.Columns) {
                update_position(this.graphColumns.Columns, sub)
              }
            })
          } else {
            element.Position = global_pos;
            global_pos++

            // change Position field in original list
            if (genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) && 
              this.selectionColumns && this.selectionColumns.Columns) {
              update_position(this.selectionColumns.Columns, element)
            } else if (genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION && 
              this.projectionColumns && this.projectionColumns.Columns) {
              update_position(this.projectionColumns.Columns, element)
            } else if (genstore.selectedItem && 
              (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || 
                genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
                genstore.selectedItem.elType === BaseItemTypes.GRAPH) && 
              this.graphColumns && this.graphColumns.Columns) {
              update_position(this.graphColumns.Columns, element)
            }
          }
        })
        this.columns_max = global_pos

        // this.selectedColumnsList = reactive(tmparr)
        // console.log(tmparr)

        // save
        this.updateColumnSettings(genstore.selectedItem, true)
      }
    },

    updateColumnDateRange(arg: ColumnListItem) {
      const genstore = generalStore();

      // change Position field in original list
      if (genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) && 
      this.selectionColumns && this.selectionColumns.Columns) {
        const found_column_index = this.selectionColumns.Columns.findIndex((col: ColumnListItem) => {
          return (col.Descriptor === ColumnSendTypeDescriptor.DateRange) && col.Selected && col.compound_id === arg.compound_id
        })
        if (found_column_index > -1) {
          // @ts-ignore
          this.selectionColumns.Columns[found_column_index].StartDate = arg.StartDate || null;
          // @ts-ignore
          this.selectionColumns.Columns[found_column_index].EndDate = arg.EndDate || null;
        }
      } else if (genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION && 
      this.projectionColumns && this.projectionColumns.Columns) {
        const found_column_index = this.projectionColumns.Columns.findIndex((col: ColumnListItem) => {
          return (col.Descriptor === ColumnSendTypeDescriptor.DateRange) && col.Selected && col.compound_id === arg.compound_id
        })
        if (found_column_index > -1) {
          // @ts-ignore
          this.projectionColumns.Columns[found_column_index].StartDate = arg.StartDate || null;
          // @ts-ignore
          this.projectionColumns.Columns[found_column_index].EndDate = arg.EndDate || null;
        }
      } else if (genstore.selectedItem && 
      (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || 
      genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
      genstore.selectedItem.elType === BaseItemTypes.GRAPH) && 
      this.graphColumns && this.graphColumns.Columns) {
        const found_column_index = this.graphColumns.Columns.findIndex((col: ColumnListItem) => {
          return (col.Descriptor === ColumnSendTypeDescriptor.DateRange) && col.Selected && col.compound_id === arg.compound_id
        })
        if (found_column_index > -1) {
          // @ts-ignore
          this.graphColumns.Columns[found_column_index].StartDate = arg.StartDate || null;
          // @ts-ignore
          this.graphColumns.Columns[found_column_index].EndDate = arg.EndDate || null;
        }
      }

      // save
      this.updateColumnSettings(genstore.selectedItem, true);
    },

    abortAllPending() {
      this.reqQueue.forEach((controller) => controller.abort())
      this.reqQueue.clear()
    },

    clearSettings() {
      // console.log('clear set')
      this.setMutating(true)
      this.clearFiltersValidation();
      this.clearFormulasValidation();
      // reactive filters change and update directly from interface
      this.selectionFilters = reactive([] as SelectionFilterGroupSend[]);
      this.projectionFilters = reactive([] as /*ProjectionFilterSend[]*/ProjectionRolesFilterGroupDto[]);
      this.tableFormulas = reactive([] as AnalyticsTableFormula[]);
      this.projectionOptions = reactive([] as AnalyticsElementOptionsDto[]);
      this.projectionParentFilters = reactive([] as SelectionFilterGroupSend[]);
      this.projectionParentChain = reactive([] as ProjectionParentInChain[]);
      // shallow reactive columns update from interface via actions because there are too many 
      // Position property updates
      this.selectionColumns = shallowReactive({} as ColumnSettingsSend);
      this.projectionColumns = shallowReactive({} as ColumnSettingsSend);
      this.graphColumns = shallowReactive({} as ColumnSettingsSend);
      this.selectedColumnsList = reactive([] as ColumnGroupItem[]);
    },

    /**
     * Массовый сброс всех фильтров текущего элемента.
     * Аналог нажатия всех крестиков удаления на каждом фильтре — полная очистка
     * содержимого каждой группы по дескриптору, in-place мутация.
     * Watcher filterSettingsMutated в Analytics.vue подхватит изменения
     * и отправит каждую группу на бэкенд через updateFilterSettings.
     * 
     * Таблица очистки по дескрипторам — см. docs/refactoring/filters-structure.md
     * 
     * TODO (рефакторинг): вынести логику очистки каждого дескриптора в отдельный модуль
     * src/models/helpers/filter-clearing.ts, чтобы и крестики (FilterListElement.deleteItem,
     * VariableDisplayList.deleteItem), и AuxSelector.resetSelection, и этот метод
     * использовали единый источник. См. план в docs/refactoring/filters-structure.md.
     */
    resetAllCurrentFilters() {
      const filters = this.getCurrentFilters;
      if (!filters || !filters.length) return;

      this.clearFiltersValidation();

      for (const group of filters) {
        this.resetFilterGroup(group);
      }
    },

    // Очистка одной группы фильтров по дескриптору
    resetFilterGroup(group: SelectionFilterGroupSend) {
      const d = group.Descriptor;

      switch (d) {
        case SelectionFilterGroupSendDescriptor.Parameters:
          this.clearParameterSelections((group as any).Parameters);
          break;

        // Флаговые / битовые маски — сброс в 0
        case SelectionFilterGroupSendDescriptor.JobStatus:
          (group as any).JobStatusFlags = 0;
          break;
        case SelectionFilterGroupSendDescriptor.FileAttachment:
          (group as any).FileAttachmentFilter = 0;
          break;
        case SelectionFilterGroupSendDescriptor.TagStatus:
          (group as any).TagStatusFlags = 0;
          break;
        case SelectionFilterGroupSendDescriptor.MovementTypes:
          (group as any).MovementTypeFilterFlags = 0;
          break;

        // Все остальные — массив Filters
        default:
          if ('Filters' in group && Array.isArray((group as any).Filters)) {
            (group as any).Filters.splice(0, (group as any).Filters.length);
          }
          break;
      }

      // Дополнительные поля для конкретных дескрипторов
      if (d === SelectionFilterGroupSendDescriptor.EntityWithRoles &&
          'SelectedCommonRoles' in group && Array.isArray((group as any).SelectedCommonRoles)) {
        (group as any).SelectedCommonRoles.splice(0, (group as any).SelectedCommonRoles.length);
      }

      if (d === SelectionFilterGroupSendDescriptor.ProjectStages) {
        (group as any).CommonRange = null;
      }

      if (d === SelectionFilterGroupSendDescriptor.LastMovementOfStorageItem &&
          'LastMovementTypeFilterFlags' in group) {
        (group as any).LastMovementTypeFilterFlags = 0;
      }

      if (d === SelectionFilterGroupSendDescriptor.StoragesOfItem) {
        (group as any).CalculateFiguresOnlySelectedStorages = false;
      }

      if (d === SelectionFilterGroupSendDescriptor.StoragesOfMovement &&
          'CommonStorageFilterTypeFlags' in group) {
        (group as any).CommonStorageFilterTypeFlags = 0;
      }
    },

    // Рекурсивная деселекция всех параметров и очистка условий
    clearParameterSelections(params: any[]) {
      if (!Array.isArray(params)) return;
      for (const param of params) {
        if (!param) continue;
        if (param.Selectable || param.Selected) {
          param.Selected = false;
          if (param.Positive !== undefined) param.Positive = false;
          if (param.Conditions) param.Conditions = [];
          if (param.Formulas) param.Formulas = [];
          if (param.TextConditions) param.TextConditions = [];
          if (param.EntityConditions) param.EntityConditions = [];
          if (param.BoolFlags !== undefined) param.BoolFlags = 0;
        }
        if (param.FieldFilters && Array.isArray(param.FieldFilters)) {
          this.clearParameterSelections(param.FieldFilters);
        }
      }
    },

    /**
     * Assign system compound_id, IdChain and compound_name fields
     * @returns void
     */
    updateColumnsIndices() {
      const genstore = generalStore()

      const that = this
      const calcind = (arr: ColumnListItem[]) => {
        const getElementName = (elem: ColumnListItem): string => {
          return (elem.Entity ? elem.Entity.Name : getSettingsTypeName(elem.Category, genstore.selectedItem?.DestType || 0))
        }
        
        // el_to_find is parent element here or undefined if root
        foreachRecursive(function a(element: any, el_to_find: any, index): any | undefined {
          if (!element) return
          
          if (element.GroupId && that.group_max < element.GroupId) {
            that.setMaxGroupIndex(element.GroupId)
          }
          if (el_to_find) {
            // Not root - add to parent values
            element.compound_id = el_to_find.compound_id+'-'+index;
            element.compound_name = el_to_find.compound_name+'.'+getElementName(element);
            element.IdChain = element.Entity ? [ ...el_to_find.IdChain, element.Entity.Id ] : el_to_find.IdChain
          } else {
            // Root - copy original values
            element.compound_id = index;
            element.compound_name = getElementName(element);
            element.IdChain = element.Entity ? [ element.Entity.Id ] : []
          }
          
          if (element.ChildColumns && element.ChildColumns.length) {
            // pass this as parent, start child index with 0
            foreachRecursive(a, element, element.ChildColumns, 0)
          }
        // pass undefined as parent, start root index with 0
        }, undefined, arr, 0)
      }

      if (genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) && 
        this.selectionColumns && this.selectionColumns.Columns) {
        calcind(this.selectionColumns.Columns)
      } else if (genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION && 
        this.projectionColumns && this.projectionColumns.Columns) {
        calcind(this.projectionColumns.Columns)
      } else if (genstore.selectedItem && 
        (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || 
          genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
          genstore.selectedItem.elType === BaseItemTypes.GRAPH) && 
        this.graphColumns && this.graphColumns.Columns) {
        calcind(this.graphColumns.Columns)

        // console.log('recalc graph columns')
      }
    },

    /**
     * Assign max group_id
     * @param compound_ids - array of element.compound_id to group; GroupId = (this.group_max || 0) + 1
     * @param ungroupId - if undefined then elements are grouped; if > 0 then compound_ids is ignored and 
     * elements with GroupId === ungroupId are ungrouped
     * @returns void
     */
    groupColumns(compound_ids: string[], ungroupId: number | undefined = undefined, ungroupElId: string | undefined = undefined) {
      const tmparr = this.selectedColumnsList as ColumnGroupItem[]

      // console.log('group')
      // console.log(compound_ids)

      tmparr.forEach((el: ColumnGroupItem, i) => {
        const genstore = generalStore()

        if ((!ungroupElId && ungroupId && el.group_id === ungroupId) ||
            (ungroupElId && ungroupId && el.group_id === ungroupId && el.sub_items && el.sub_items.length) || 
            (compound_ids.includes(el.compound_id) && el.group_id === null)) {
          // update GroupId in base list

          const that = this
          const update_group = (arr: ColumnSendSelectable[]) => {
            foreachRecursive(function a(element: any, el_to_find: any): any | undefined {
              if (!element) return

              if (el_to_find && el_to_find.Descriptor !== 2 && 
                ((ungroupElId == undefined && ungroupId && element.GroupId === ungroupId) ||
                 (ungroupElId != undefined && ungroupId && element.GroupId === ungroupId && element.compound_id === ungroupElId) || 
                  el_to_find.compound_id === element.compound_id)) {

                if (ungroupElId == undefined && ungroupId && element.GroupId === ungroupId) {
                  console.log('de-assign group id '+ungroupId)
                  element.GroupId = null
                } else if (ungroupElId != undefined && ungroupId && element.GroupId === ungroupId && 
                  element.compound_id === ungroupElId) {
                  console.log('de-assign group id '+ungroupId+" and el id "+ungroupElId)
                  element.GroupId = null
                } else {
                  // console.log('assign group id '+(that.group_max+1))
                  element.GroupId = (that.group_max || 0)+1
                }
              }
              
              if (element.ChildColumns && element.ChildColumns.length) {
                foreachRecursive(a, el_to_find, element.ChildColumns)
              }
    
              return
            }, el, arr, this.group_max)
          }

          if (genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) && 
            this.selectionColumns && this.selectionColumns.Columns) {
            update_group(this.selectionColumns.Columns)
            // reassign to trigger redraw getter
            this.selectionColumns.Columns = [...this.selectionColumns.Columns]
    
          } else if (genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION && 
            this.projectionColumns && this.projectionColumns.Columns) {
            update_group(this.projectionColumns.Columns)
            // reassign to trigger redraw getter
            this.projectionColumns.Columns = [...this.projectionColumns.Columns]
          } else if (genstore.selectedItem && 
            (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || 
              genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
              genstore.selectedItem.elType === BaseItemTypes.GRAPH) && 
            this.graphColumns && this.graphColumns.Columns) {
            update_group(this.graphColumns.Columns)
            // reassign to trigger redraw getter
            this.graphColumns.Columns = [...this.graphColumns.Columns]
          }
        }
      })

      this.group_max++

      // change Position field in original list
      this.updateColumnSelection(undefined, true)

      // save
      // setTimeout(() => {
      //   const genstore = generalStore()
      //   this.updateColumnSettings(genstore.selectedItem, true)
      // }, 500)
    },

    /**
     * Update Selected and non-Selected status. 
     * Perform all operations in shallow reactive columns lists and then reassign them for getters to trigger
     * @returns void
     */
    async updateColumnSelection(el_to_find: any, save_after: boolean = false) {
      // console.log('update selection in store')
      const genstore = generalStore()

      // Check TheOnlyColumnForCategory in this.selectedColumnsList first
      // if newly selected element is "the only for the category" then CategoryToStrip is not null; otherwise CompoundIdToStrip is not null if this element is selected
      const CompoundIdToStrip = el_to_find && !(el_to_find as ColumnSendSelectable).TheOnlyColumnForCategory ? 
      findRecursive(function a(element: any, el_to_find: any): any | undefined {
        if (!element) return undefined

        if (element.TheOnlyColumnForCategory && element.Category === (el_to_find as ColumnSend).Category) {
          return element.compound_id;
        }
        if (element.sub_items) {
          return findRecursive(a, el_to_find, element.sub_items)
        }
        return undefined
      }, el_to_find, _.cloneDeep(this.selectedColumnsList)) : 
      undefined;
      const CategoryToStrip = el_to_find && !CompoundIdToStrip && (el_to_find as ColumnSendSelectable).TheOnlyColumnForCategory ? (el_to_find as ColumnSend).Category : undefined;
      
      // check if only one element is in the group
      const GroupToStrip = new Set<any>()
      this.selectedColumnsList.forEach(element => {
        // filter out only elements that are not going to be stripped from the selected list
        if (element.sub_items && element.GroupId) {
          if (element.sub_items.filter(sub => {
            return sub.GroupId && ((!CompoundIdToStrip && !CategoryToStrip) || !(CompoundIdToStrip && sub.compound_id === CompoundIdToStrip) &&
              !(CategoryToStrip && sub.Category === CategoryToStrip && sub.compound_id !== el_to_find.compound_id))
          }).length <= 1) {
            GroupToStrip.add(element.GroupId)
          }
        }
      });

      this.selectedColumnsList = []
      let tmp_arr: ColumnGroupItem[] = []
      const that = this

      // this is to add new selected elements; Position can duplicate here
      const update_selection = (arr: ColumnSendSelectable[]) => {
        foreachRecursive(function a(element: any, el_to_find: any): any | undefined {
          if (!element) return
          
          if (el_to_find && el_to_find.Descriptor !== 2 && el_to_find.compound_id === element.compound_id) {
            if ((el_to_find as ColumnGroupItem).Selected) {
              element.Position = (that.columns_max || 0)+1
            } else {
              // reset position if element selection is unchecked
              element.Position = -1
              element.GroupId = null
            }
            element.Selected = el_to_find.Selected
          }

          // uncheck according to "the only column for category"
          if (CompoundIdToStrip && element.compound_id === CompoundIdToStrip) {
            // reset position if element selection is unchecked
            element.Position = -1
            element.GroupId = null
            element.Selected = false
          } else if (CategoryToStrip && element.Category === CategoryToStrip && element.compound_id !== el_to_find.compound_id) {
            element.Position = -1
            element.GroupId = null
            element.Selected = false
          }

          // if only one item in group left then ungroup all
          if (GroupToStrip.has(element.GroupId)) {
            element.GroupId = null
            element.Position = -1;
          }
          
          if (element.ChildColumns && element.ChildColumns.length) {
            foreachRecursive(a, el_to_find, element.ChildColumns)
          }

          // add to selected list
          if (element.Descriptor !== 2 && (element as ColumnGroupItem).Selected) {
            // tmp_arr.push(element)
            if (element.GroupId == null) {
              tmp_arr.push({
                ...element,
                group_id: null
              })
            } else  {
              const group_el = tmp_arr.find(el => { return el.group_id !== null && el.group_id === element.GroupId })
              if (!group_el) {
                const push_el = element
                push_el.sub_items = undefined
                push_el.going_to_group = false
                tmp_arr.push({
                  ...element,
                  group_id: element.GroupId,
                  compound_name: '',
                  compound_id: '',
                  IdChain: [],
                  sub_items: reactive([push_el])
                })
              } else {
                const push_el = element
                push_el.sub_items = undefined
                push_el.going_to_group = false
                if (group_el.sub_items) {
                  group_el.sub_items.push(push_el)
                } else {
                  group_el.sub_items = reactive([push_el])
                }
              }
            }
          }
          return
        }, el_to_find, arr, this.columns_max)
      }

      if (genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) && 
        this.selectionColumns && this.selectionColumns.Columns) {
        update_selection(this.selectionColumns.Columns)
      } else if (genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION && 
      this.projectionColumns && this.projectionColumns.Columns) {
        update_selection(this.projectionColumns.Columns)
      } else if (genstore.selectedItem && 
        (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || 
          genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
          genstore.selectedItem.elType === BaseItemTypes.GRAPH) && 
        this.graphColumns && this.graphColumns.Columns) {
        update_selection(this.graphColumns.Columns)
      }

      // sort and then recalc position
      tmp_arr = tmp_arr.sort((el1, el2) => {
        return (el1 as ColumnListItem).Position - (el2 as ColumnListItem).Position
      })

      // remove gaps between Position fields if some elements were deleted
      // this changes only tmp_arr
      let global_pos = 0;
      for (let e = 0; e < tmp_arr.length; e++) {
        const element = tmp_arr[e]
      
        // index throughout child arrays
        // sort sub_items and then recalc global position
        if (element.sub_items) {
          element.Position = global_pos;
          element.sub_items = reactive(element.sub_items.sort((el1, el2) => {
            return (el1 as ColumnListItem).Position - (el2 as ColumnListItem).Position
          }))

          element.sub_items.forEach(sub => {
            sub.Position = global_pos;
            global_pos++
          })
        } else {
          element.Position = global_pos;
          global_pos++
        }
      }
      this.columns_max = global_pos

      // trigger reactive redraw
      this.selectedColumnsList = reactive(tmp_arr)

      // restore sorted and gap-free Positions to each element in the main list
      const restore_positions = (arr: ColumnSendSelectable[]) => {
        foreachRecursive(function a(element: any, el_to_find: any): any | undefined {
          if (!element) return
          
          const tmpel = findRecursive(function a(element: any, el_to_find: any): any | undefined {
            if (!element) return undefined
    
            if (element.compound_id === el_to_find.compound_id) {
              return element;
            }
            if (element.sub_items) {
              return findRecursive(a, el_to_find, element.sub_items)
            }
            return undefined
          }, element, that.selectedColumnsList)
          
          if (tmpel) {
            element.Position = tmpel.Position
            element.Selected = tmpel.Selected
          } else {
            element.Position = -1
            element.Selected = false
            element.GroupId = null
          }
          if (element.ChildColumns && element.ChildColumns.length) {
            foreachRecursive(a, el_to_find, element.ChildColumns)
          }
          return
        }, undefined, arr)
      }

      if (genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) && 
        this.selectionColumns && this.selectionColumns.Columns) {
        restore_positions(this.selectionColumns.Columns)
        // reassign to trigger redraw getter
        this.selectionColumns.Columns = [...this.selectionColumns.Columns]
      } else if (genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION && 
      this.projectionColumns && this.projectionColumns.Columns) {
        restore_positions(this.projectionColumns.Columns)
        // reassign to trigger redraw getter
        this.projectionColumns.Columns = [...this.projectionColumns.Columns]
      } else if (genstore.selectedItem && 
        (genstore.selectedItem.elType === BaseItemTypes.BAR_CHART || 
          genstore.selectedItem.elType === BaseItemTypes.PIE_CHART || 
          genstore.selectedItem.elType === BaseItemTypes.GRAPH) && 
          this.graphColumns && this.graphColumns.Columns) {
        restore_positions(this.graphColumns.Columns)
        // reassign to trigger redraw getter
        this.graphColumns.Columns = [...this.graphColumns.Columns]
      }

      // save
      if (el_to_find || save_after) {
        const genstore = generalStore()
        
        // setTimeout(() => {
          if (save_after) { 
            // console.log('after group')
          }

          await this.updateColumnSettings(genstore.selectedItem, true)
        // }, 100)
      }
    },

  },

  getters: {
    isLoading(state): boolean {
      return state.loading
    },

    isMutating(state): boolean {
      return state.mutating
    },

    isSaving(state): boolean {
      return state.saving
    },

    getFilterValidation(state): any[] {
      return state.filterValidation
    },

    getFormulaValidation(state): any[] {
      return state.formulaValidation
    },

    maxColumnIndex(state): number {
      return state.columns_max
    },

    maxGroupIndex(state): number {
      return state.group_max
    },

    getProjectionParentChain(state) {
      return state.projectionParentChain
    },

    getProjectionFilters(state) {
      return state.projectionFilters
    },

    getCurrentFilters(state): SelectionFilterGroupSend[] {
      const genstore = generalStore();
      return genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) ? 
        state.selectionFilters : 
          (genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION ?
          state.projectionParentFilters : [])
    },

    getTableFormulas(state): AnalyticsTableFormula[] {
      return state.tableFormulas
    },

    getProjectionOptions(state): AnalyticsElementOptionsDto[] {
      return state.projectionOptions
    },

    getSelectedColumns(state): ColumnListItem[] {
      return state.selectedColumnsList
    },

    getNonSelectedColumns(state): ColumnListItem[] {
      const genstore = generalStore()

      const filtered_get = (arr: ColumnListItem[]) => {
        return mapRecursive(function a(element: any, parent: any) {
          return {
            ...element,
            ChildColumns: element.ChildColumns ? 
              mapRecursive(a, element, element.ChildColumns.filter((el: ColumnListItem) => !el.Selected)) : 
              []
          }
        // @ts-ignore
        }, undefined, arr.filter(el => !(el as ColumnListItem).Selected))
      }

      return genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) ? 
        (state.selectionColumns && state.selectionColumns.Columns ? 
          filtered_get(state.selectionColumns.Columns) : 
          [] as ColumnListItem[]) : (
          genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION ?
          (state.projectionColumns && state.projectionColumns.Columns ? 
            filtered_get(state.projectionColumns.Columns) : 
            [] as ColumnListItem[]) :
            (state.graphColumns && state.graphColumns.Columns ? 
              filtered_get(state.graphColumns.Columns) : 
              [] as ColumnListItem[])
        )
    },

    getAllColumns(state): ColumnListItem[] {
      const genstore = generalStore()
      return genstore.selectedItem && [BaseItemTypes.SELECTION, BaseItemTypes.LIST].includes(genstore.selectedItem.elType) ? 
        (state.selectionColumns && state.selectionColumns.Columns ? 
          state.selectionColumns.Columns : 
          [] as ColumnListItem[]) : (
          genstore.selectedItem && genstore.selectedItem.elType === BaseItemTypes.PROJECTION ?
          (state.projectionColumns && state.projectionColumns.Columns ? 
            state.projectionColumns.Columns : 
            [] as ColumnListItem[]) :
            (state.graphColumns && state.graphColumns.Columns ? 
              state.graphColumns.Columns : 
              [] as ColumnListItem[])
        )
    },
  }
});
